import { fetchMontlyList, addMonthlyList, deleteMontlyList, addMontlyListCart, changeLoading } from "../../features/productSlice";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect, useCallback } from "react";
import { fetchCart } from "../../features/cartSlice";
import {
  Container, Form, Button, Row, Table, Col, Image, Modal, InputGroup, ToastContainer, Toast
} from "react-bootstrap";
import { NavLink } from "react-router-dom";

function MonthlyList() {
  const dispatch = useDispatch();
  const { monthlyListProduct, deleteSuccess } = useSelector((state) => state.product);

  const [cartId, setCartId] = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [isLoading, setIsLoading] = useState(false);        // add-to-cart buttons
  const [products, setProducts] = useState([]);             // local mirror for UI
  const [show, setShow] = useState(false);                  // toast
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);    // infinite scroll fetch
  const limit = 12;

  // Fetch a page safely
  const loadMonthlyList = useCallback(async (currentOffset) => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const res = await dispatch(fetchMontlyList({ offset: currentOffset, limit })).unwrap();
      const fetched = res?.data?.result?.length || 0;
      if (fetched < limit) setHasMore(false);
      setOffset(currentOffset + fetched);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [dispatch, hasMore, limit, loadingMore]);

  // mirror redux list to local state & initial load
  useEffect(() => {
    if (monthlyListProduct.length === 0 && hasMore && !loadingMore) {
      loadMonthlyList(0);
    }
    setProducts(monthlyListProduct);
  }, [monthlyListProduct, loadMonthlyList, hasMore, loadingMore]);

  // Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      const nearBottom = window.innerHeight + document.documentElement.scrollTop + 100 >= document.documentElement.offsetHeight;
      if (nearBottom && !loadingMore && hasMore) {
        loadMonthlyList(offset);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [offset, loadingMore, hasMore, loadMonthlyList]);

  // Quantity +
  const handleIncrement = (pid, iitem, qty) => {
    if (isLoading) return;
    setProducts(prev =>
      prev.map(p => p.productItemId === iitem ? { ...p, quantity: p.quantity + 1 } : p)
    );
    dispatch(addMonthlyList({ productId: pid, productItemId: iitem, quantity: qty + 1 }));
  };

  // Quantity -
  const handleDecrement = (pid, iitem, qty) => {
    if (isLoading || qty <= 1) return;
    setProducts(prev =>
      prev.map(p => (p.productItemId === iitem && p.quantity > 1) ? { ...p, quantity: p.quantity - 1 } : p)
    );
    dispatch(addMonthlyList({ productId: pid, productItemId: iitem, quantity: qty - 1 }));
  };

  // Delete flow
  const handleMonthlyDelete = (id) => {
    setCartId(id ? [id] : cartId);
    setModalShow(true);
  };
  const handleDelete = async () => {
    await dispatch(deleteMontlyList({ wishlistId: cartId.join(",") }));
    setModalShow(false);
  };

  // ---- soft cart refresh (no polling) ----
  const refreshCartSoft = useCallback(() => {
    dispatch(fetchCart());                // immediate
    setTimeout(() => dispatch(fetchCart()), 400); // one more after a short delay
  }, [dispatch]);

  // Add ALL to cart
  const addAllCart = async () => {
    if (isLoading) return;
    setIsLoading(true);
    setCartId([]);
    try {
      await dispatch(addMontlyListCart(null)).unwrap();
      refreshCartSoft();      // no loop, just 1 immediate + 1 delayed
      setShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Add SELECTED to cart
  const addCart = async () => {
    if (cartId.length === 0 || isLoading) return;
    setIsLoading(true);
    try {
      await dispatch(addMontlyListCart(cartId)).unwrap();
      refreshCartSoft();
      setCartId([]);
      setShow(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setModalShow(false);
    setCartId([]);
  };

  const handleSelectId = (id) => {
    setCartId(prev => prev.includes(id) ? prev.filter(el => el !== id) : [...prev, id]);
  };

  // After delete, refresh list cleanly
  useEffect(() => {
    if (deleteSuccess) {
      dispatch(changeLoading());         // resets product slice lists/flags
      setOffset(0);
      setHasMore(true);
      loadMonthlyList(0);
      setCartId([]);
    }
  }, [deleteSuccess, dispatch, loadMonthlyList]);

  return (
    <div>
      <Container fluid>
        <Row>
          <Col xs={12} sm={4} className="text-start">
            <Button
              className="bg-secondary border-secondary"
              onClick={() => handleMonthlyDelete(null)}
              disabled={cartId.length <= 0}
            >
              Delete
            </Button>
          </Col>
          <Col xs={12} sm={8} className="text-end">
            <Button className="me-5" onClick={addCart} disabled={cartId.length <= 0 || isLoading}>
              Add selected items to cart
            </Button>
            <Button className="bg-dark border-dark" onClick={addAllCart} disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Adding...
                </>
              ) : (
                "Add all to Cart"
              )}
            </Button>
          </Col>
        </Row>

        <Table bordered responsive hover className="mt-3">
          <tbody>
            {products.length > 0 && products.map((ml) => (
              <tr key={ml.wishlistId ?? `${ml.productItemId}-${ml.productID}`}>
                <td className="col-1 text-center align-middle">
                  {ml.stock > 0 && (
                    <Form.Check
                      className="monthlyListCheck"
                      type="checkbox"
                      onChange={() => handleSelectId(ml.wishlistId)}
                      id={`ml-${ml.wishlistId}`}
                      checked={cartId.includes(ml.wishlistId)}
                    />
                  )}
                </td>
                <td className="col-6">
                  <Row className="border-end">
                    <Col sm={2}>
                      <NavLink to={`/pd/${ml.slug}`}>
                        <Image
                          src={`${process.env.REACT_APP_IMAGE_URL}/uploads/${ml.image[0]}`}
                          thumbnail
                          className="mt-2"
                        />
                      </NavLink>
                    </Col>
                    <Col sm={10}>
                      <p>
                        <NavLink to={`/pd/${ml.slug}`}>
                          <b>{ml.productName}</b>
                        </NavLink>
                      </p>
                      <p className="text-secondary">{ml.brandName}</p>
                      <p><b>{ml.unit} {ml.unitName}</b></p>
                    </Col>
                  </Row>
                </td>
                <td className="col-4 align-middle">
                  <div className="input-group mb-3 w-75 m-auto">
                    <InputGroup.Text className="cursor-pointer" onClick={() => handleDecrement(ml.productID, ml.productItemId, ml.quantity)}>-</InputGroup.Text>
                    <input type="number" min="1" className="form-control p-1 text-center" value={ml.quantity} readOnly aria-label="Amount" />
                    <InputGroup.Text className="cursor-pointer" onClick={() => handleIncrement(ml.productID, ml.productItemId, ml.quantity)}>+</InputGroup.Text>
                  </div>
                </td>
                <td className="col-2">
                  <p className="text-secondary">
                    MRP: <s>{ml.mrpPrice.toFixed(2)}</s>&nbsp;
                    <span className="text-dark"><b>{ml.salePrice.toFixed(2)}</b></span>
                  </p>
                  <p className="text-secondary">
                    {ml.quantity} x {ml.salePrice.toFixed(2)} ={" "}
                    <span className="text-dark"><b>{(ml.quantity * ml.salePrice).toFixed(2)}</b></span>
                  </p>
                </td>
                <td className="col-1 text-center align-middle">
                  <p className="text-danger cursor-pointer" onClick={() => handleMonthlyDelete(ml.wishlistId)}>
                    Delete
                  </p>
                </td>
              </tr>
            ))}
            {loadingMore && (
              <tr>
                <td colSpan={5} className="text-center">
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Loading more…
                </td>
              </tr>
            )}

            {!loadingMore && products.length === 0 && (
              <tr>
                <td className="text-danger text-center">No list as of now.</td>
              </tr>
            )}
          </tbody>
        </Table>
      </Container>

      <ToastContainer className="p-3" position="middle-center" style={{ zIndex: 2000 }}>
        <Toast bg="success" onClose={() => setShow(false)} show={show} delay={3000} autohide>
          <Toast.Body className="text-white text-center">
            <b>Added successfully</b>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <Modal size="md" centered backdrop="static" keyboard={false} show={modalShow} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger m-auto h3">Warning</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="h4 text-center">Are you sure? You want to delete?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleDelete}>Yes Delete!</Button>
          <Button className="bg-secondary border-secondary" onClick={handleClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default MonthlyList;
